import authenticationRoutes from '../../authentication/components/Routes';
import authSimplifiedRoutes from '../../authSimplified/components/Routes';
import GeneralContentPage from '../../generalContent/components/GeneralContentPage';
import React, { LazyExoticComponent } from 'react';
import taskListRoutes, { taskRoutes } from '../../taskList/components/Routes';
import withTaskFlow from '../../taskList/components/WithTaskFlow';
import { Route, Routes, useLocation } from 'react-router-dom';
import { routeMappings } from '../../constants';
import TcPlusSplashPage from './TcPlusSplashPage';
import { withBasicDetails } from './WithBasicDetails';
import { AnimatePresence } from 'framer-motion';
import configurablePageRoutes from '../../workflowConfigurablePages/components/Routes';
import { ReAttemptPage } from '../../staticPages/components/ReAttemptPage';
import basicTaskListRoutes from '../../basicTaskList/components/Routes';
import { useAppSelector } from '../../store/hook';
import { SplashPage } from '../../staticPages/components/SplashPage';
import { HomePage } from '../../home/components/HomePage';
import { ResponseReuseMessagePage } from '../../staticPages/components/ResponseReuseMessagePage';
import { isJourneyTheme } from '../../common/utils/fallbackCondition';
import { useTranslation } from 'react-i18next';
import { mfsRoutes, raterNominationRoutes, surveyRoutes } from '../../survey/components/MfsRoutes';

const BiodataPage = React.lazy(() => import('../../biodata/components/BiodataPage'));
const DPNPage = React.lazy(() => import('../../dpn/components/DpnPage'));
const ErrorPage = React.lazy(() => import('../../staticPages/components/ErrorPage'));
const ExpiredLinkPage = React.lazy(() => import('../../staticPages/components/ExpiredLinkPage'));
const FeedbackPage = React.lazy(() => import('../../candidateFeedback/components/CandidateFeedbackPage'));
const SurveyTaskListPage = React.lazy(() => import('../../survey/components/surveyTaskHomePage/SurveyTaskListPage'));
const CandidateConsentPage = React.lazy(() => import('../../proctoring/components/CandidateConsentPage'));
const ProctoringCameraSetupPage = React.lazy(() => import('../../proctoring/components/ProctoringCameraSetupPage'));
const LinkInvalidPage = React.lazy(() => import('../../staticPages/components/LinkInvalidPage'));
const LockDownBrowserPage = React.lazy(() => import('../../staticPages/components/LockDownBrowserPage'));
const LeaderboardPage = React.lazy(() => import('../../leaderBoard/components/LeaderboardPage'));

export interface CustomProps {
  pageName: string;
}
export interface RoutePageProps {
  customProps?: CustomProps;
  routes?: RouteParams[];
}

export interface RouteParams {
  path: string;
  exact?: boolean;
  strict?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: LazyExoticComponent<(props: any) => React.JSX.Element> | ((props: RoutePageProps) => React.JSX.Element);
  routes?: RouteParams[];
  customProps?: CustomProps;
}

const routes: RouteParams[] = [
  {
    path: routeMappings['INIT_TCPLUS_EXPERIENCE'],
    exact: true,
    component: TcPlusSplashPage,
  },
  {
    path: routeMappings['INIT_EXPERIENCE'],
    exact: true,
    component: withBasicDetails(SplashPage),
  },
  {
    path: `${routeMappings['LANDING_PAGE']}/:id`,
    exact: true,
    component: withBasicDetails(HomePage),
    customProps: {
      pageName: 'landingPageTitle',
    },
  },
  {
    path: routeMappings['DPN'],
    exact: true,
    component: DPNPage,
    customProps: {
      pageName: 'dpnTitle',
    },
  },
  {
    path: routeMappings['LEADERBOARD'],
    exact: true,
    component: LeaderboardPage,
    customProps: {
      pageName: 'leaderBoard',
    },
  },
  {
    path: routeMappings['CANDIDATE_CONSENT'],
    exact: true,
    component: withBasicDetails(CandidateConsentPage),
    customProps: {
      pageName: 'candidateConsentTitle',
    },
  },
  {
    path: `${routeMappings['GENCONTENT']}/:id`,
    exact: true,
    component: withBasicDetails(GeneralContentPage),
    customProps: {
      pageName: 'genContentTitle',
    },
  },
  {
    path: `${routeMappings['MFSTASKLIST']}/:id`,
    exact: true,
    component: withBasicDetails(SurveyTaskListPage),
  },
  {
    path: routeMappings['SECURE_BROWSER'],
    exact: true,
    component: LockDownBrowserPage,
    customProps: {
      pageName: 'secureBrowserTitle',
    },
  },
  {
    path: routeMappings['EXPIRED_LINK'],
    exact: true,
    component: ExpiredLinkPage,
    customProps: {
      pageName: 'linkExpiredTitle',
    },
  },
  {
    path: routeMappings['LINK_INVALID'],
    exact: true,
    component: LinkInvalidPage,
    customProps: {
      pageName: 'linkInvalidTitle',
    },
  },
  {
    path: routeMappings['RE_ATTEMPT'],
    exact: true,
    component: withBasicDetails(ReAttemptPage),
    customProps: {
      pageName: 'reAttemptTitle',
    },
  },
  {
    path: routeMappings['VIDEO_FEEDBACK'],
    exact: true,
    component: withBasicDetails(withTaskFlow(FeedbackPage)),
    customProps: {
      pageName: 'videoFeedbackTitle',
    },
  },
  {
    path: routeMappings['FEEDBACK'],
    exact: true,
    component: withBasicDetails(FeedbackPage),
    customProps: {
      pageName: 'feedbackTitle',
    },
  },
  {
    path: routeMappings['PROCTORING_SETUP'],
    exact: true,
    component: withBasicDetails(ProctoringCameraSetupPage),
    customProps: {
      pageName: 'proctoringTitle',
    },
  },
  {
    path: `${routeMappings['BIODATA']}/:id`,
    exact: true,
    component: withBasicDetails(BiodataPage),
  },
  {
    path: `${routeMappings['INFO']}/:responseReuseType`,
    exact: true,
    component: withBasicDetails(ResponseReuseMessagePage),
  },
  {
    path: '*',
    exact: true,
    component: ErrorPage,
    customProps: {
      pageName: 'errorTitle',
    },
  },
];

export const RouteWithSubRoutes = (route: RouteParams): React.JSX.Element => {
  return (
    <Route path={route.path} element={<route.component customProps={route.customProps} routes={route.routes} />} />
  );
};

const Wrapper = ({ animate, children }: { animate: boolean; children: React.JSX.Element }): React.JSX.Element => {
  if (animate) {
    return (
      <AnimatePresence mode="wait" initial={false}>
        {children}
      </AnimatePresence>
    );
  }
  return children;
};

const allRoutes = [
  ...authenticationRoutes,
  ...authSimplifiedRoutes,
  ...basicTaskListRoutes,
  ...taskListRoutes,
  ...taskRoutes,
  ...configurablePageRoutes,
  ...routes,
  ...mfsRoutes,
  ...surveyRoutes,
  ...raterNominationRoutes,
];

const RoutesComponent = (): React.JSX.Element => {
  const template = useAppSelector((state) => state.template);
  const { t } = useTranslation();
  const location = useLocation();

  const PageTitle = ({ title }: { title: string }): JSX.Element => {
    document.title = title;
    return <></>;
  };

  return (
    <Wrapper animate={isJourneyTheme(template.layoutCode)}>
      <Routes location={location} key={location.pathname}>
        {allRoutes.map((route: RouteParams, i: number) => {
          const customPageName = route.customProps?.pageName ? `${t(route.customProps?.pageName)} | ` : '';
          const pageTitle = `${customPageName}${template.settings.mfsEnabled ? t('shl360') : t('shlExperiences')}`;
          return (
            <Route
              key={i}
              path={route.path}
              element={
                <>
                  <PageTitle title={pageTitle} />
                  <route.component customProps={route.customProps} routes={route.routes} />
                </>
              }
            />
          );
        })}
      </Routes>
    </Wrapper>
  );
};

export default RoutesComponent;
